import { Link } from "react-router-dom";
import { Sparkles, ArrowUpRight } from "lucide-react";

/** Helper — render a localised price (no decimals when whole, 2dp otherwise). */
export function formatPrice(amount, currency) {
  if (!amount || amount <= 0) return "";
  const n = Number(amount);
  const formatter = new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: currency || "GBP",
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return formatter.format(n);
}

/**
 * Wellness product card — premium feel, large image, calm typography.
 * Sized for a 1-2-3 column responsive grid.
 *
 * `variant="featured"` uses a wider, taller, gradient-edged card for the
 * editorial-first /shop hero strip. `variant="grid"` is the standard card.
 */
export default function ProductCardV2({ product, variant = "grid" }) {
  if (!product) return null;
  const heroUrl = product.hero_image?.url || product.gallery?.[0]?.url || "";
  const bestFor = product.best_for || [];
  const isFeatured = variant === "featured";

  return (
    <Link
      to={`/shop/${product.slug}`}
      className={`group block ${isFeatured ? "sm:col-span-2" : ""}`}
      data-testid={`shop-card-${product.slug}`}
    >
      <article
        className={`relative overflow-hidden rounded-3xl bg-white border border-emerald-100 transition hover:shadow-lg hover:border-emerald-200 ${isFeatured ? "sm:grid sm:grid-cols-[1.05fr_1fr]" : ""}`}
      >
        {/* Image */}
        <div className={`relative bg-gradient-to-br from-emerald-50/40 via-amber-50/30 to-white overflow-hidden ${isFeatured ? "aspect-[4/3] sm:aspect-auto" : "aspect-[4/3]"}`}>
          {heroUrl ? (
            <img
              src={heroUrl}
              alt={product.name}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-emerald-700/30">
              <Sparkles className="w-8 h-8" strokeWidth={1.4} />
            </div>
          )}
          {isFeatured && (
            <span className="absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-semibold px-2.5 py-1 rounded-full bg-emerald-700 text-white">
              <Sparkles className="w-3 h-3" /> Featured
            </span>
          )}
        </div>

        {/* Copy */}
        <div className={`p-5 sm:p-6 ${isFeatured ? "sm:p-8 flex flex-col justify-center" : ""}`}>
          {product.category && (
            <p className="text-[10px] uppercase tracking-[0.18em] text-emerald-700/80 font-semibold mb-1.5">
              {product.category.replace(/_/g, " ")}
            </p>
          )}
          <h3 className={`font-display ${isFeatured ? "text-2xl sm:text-3xl" : "text-lg"} text-emerald-950 font-light leading-snug`}>
            {product.name}
          </h3>
          {product.short_description && (
            <p className={`text-emerald-900/75 mt-2 leading-relaxed ${isFeatured ? "text-base" : "text-[13px] line-clamp-2"}`}>
              {product.short_description}
            </p>
          )}
          {bestFor.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {bestFor.slice(0, isFeatured ? 4 : 3).map((b) => (
                <span key={b} className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-100">
                  {b}
                </span>
              ))}
            </div>
          )}
          <div className="mt-5 flex items-center justify-between">
            <div>
              {product.price > 0 && (
                <p className={`font-display ${isFeatured ? "text-2xl" : "text-xl"} text-emerald-950 leading-none`}>
                  {formatPrice(product.price, product.currency)}
                </p>
              )}
              {product.allows_subscription && product.subscription_price > 0 && product.subscription_price < product.price && (
                <p className="text-[11px] text-emerald-700 mt-1">
                  or {formatPrice(product.subscription_price, product.currency)}/mo subscribe & save
                </p>
              )}
            </div>
            <span className="inline-flex items-center text-sm text-emerald-700 group-hover:text-emerald-800 transition" aria-hidden="true">
              Read & try <ArrowUpRight className="w-4 h-4 ml-0.5" />
            </span>
          </div>
        </div>
      </article>
    </Link>
  );
}
